import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import type { HTMLAttributes } from 'react';
import {
  dragId,
  SECTION_NAMES,
  useSidebarDrag,
  zoneId,
  type DragData,
  type DragZone,
} from './sidebar-dnd.util';
import type { SidebarSection } from './sidebar-layout.util';
import { useSidebarPins } from './sidebar-menu.util';
import type { TabsNavItem } from './use-tabs-nav.hook';

/**
 * Makes a sidebar item draggable, and a place to drop beside when it is a
 * pin. The item keeps its own role and tab stop, and its own keys; the whole
 * item is the handle, picked up with the pointer or with Alt and an arrow.
 * Outside the tabbed sidebar's pins context, until the saved layout loads,
 * and while `fixed` (a chat still waiting for its id), nothing drags.
 */
export function useSortablePin(
  zone: DragZone,
  item: TabsNavItem,
  preview: DragData['preview'],
  fixed = false
): {
  nodeRef: (node: HTMLElement | null) => void;
  handleProps: HTMLAttributes<HTMLElement>;
  isDragging: boolean;
} {
  const pins = useSidebarPins();
  const data: DragData = {
    zone,
    key: item.key,
    ref: item.ref,
    label: item.label,
    item,
    preview,
  };
  // Nothing drags until the saved layout is back to take the drop.
  const enabled = pins !== null && pins.layout.ready && !fixed;
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: dragId(zone, item.key),
    data,
    disabled: !enabled,
  });
  return {
    nodeRef: setNodeRef,
    handleProps: enabled
      ? { 'aria-describedby': attributes['aria-describedby'], ...listeners }
      : {},
    isDragging,
  };
}

/**
 * A section's own drop area, for its empty space: a drop there goes to its
 * end. `dragging` lets an empty section grow tall enough to take a drop.
 */
export function useDropZone(section: SidebarSection, empty: boolean) {
  const { activeKey, target } = useSidebarDrag();
  const { setNodeRef } = useDroppable({
    id: zoneId(section),
    data: { zone: section, key: null, label: SECTION_NAMES[section] },
  });
  return {
    zoneRef: setNodeRef,
    dragging: activeKey !== null,
    /** The line for a drop into the section while it is empty. */
    emptyLine: empty && target?.section === section,
  };
}
